import { BarElement, CategoryScale, Chart as ChartJS, Legend, LinearScale, Title, Tooltip } from 'chart.js';
import React from 'react';
import { DashBoardDonutCard, IDashboardDonutChartDataSet } from '../../../components/UI-Elements/DashBoardDonutCard';
import { StackedBarChartElement } from '../../../sp-types/dashboardTypes';
import { formatToShortString, FormatToShortPriceTemplate } from '../../../utils/FormatValue';
import { GENERAL_ID } from '../../../components/Stores/exposureDataStore';
import { getUnitObj } from '../../../utils/MassUnits/massUnitsUtils';
import { StackedBarChart } from '../StackedBarDoughnutChartCard';
import { AttachMoney, Inventory, DateRange, ViewWeek, ShowChart, Receipt, Close } from '@mui/icons-material';
import { useMaterialsStore } from '../../../components/Stores/MaterialsStore';
import { useAccountStore } from '../../../components/Stores/accountStore';
import { useFormulasPrices } from '../../../hooks/useFormulasPrices';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

interface Props {
  monthExposures: StackedBarChartElement[];
  accumulated: StackedBarChartElement[];
  cardTitle: string;
  cardDescription: string;
  cardUrl: string;
  formulaId: string;
  currentDate?: Date;
  addNewUrl?: string;
  terminals: number[];
  isTerminals: boolean;
  setIsTerminals: (_isTerminals: boolean) => void;
  isQuantity: boolean;
  setIsQuantity: (_isQuantity: boolean) => void;
  showPriceToggle?: boolean;
  onClose?: () => void;
}

const emptyMockData: StackedBarChartElement[] = [
  {
    formulaId: '',
    color: '#EAEAEB',
    fieldName: 'No material',
    valueArray: [],
    quantityArray: [],
    monetaryArray: [],
    sumQuantity: 0,
    sumMonetary: 0,
  } as any,
];

export const ChartDetailsDialog: React.FC<Props> = ({
  cardTitle,
  monthExposures,
  accumulated,
  currentDate = new Date(),
  formulaId,
  isTerminals,
  setIsTerminals,
  terminals,
  isQuantity,
  setIsQuantity,
  showPriceToggle = false,
  onClose,
}) => {
  const { materialsMap } = useMaterialsStore();
  const { account } = useAccountStore();
  const baseCurrency = account?.baseCurrency || 'USD';
  const { data: pricesData } = useFormulasPrices();
  const spotPricesMap = pricesData?.pricesMap || {};
  const [isActualPrice, setIsActualPrice] = React.useState(false);

  // Function to convert a single StackedBarChartElement to IDashboardDonutChartDataSet
  function convertToDonutChartDataSet(element: StackedBarChartElement, currentDate: Date): IDashboardDonutChartDataSet {
    // Use sumValue if available (total across all months), otherwise use current month value
    const value = element.sumValue !== undefined 
      ? Number(element.sumValue) // Ensure it's a number, not a string
      : Number(element.valueArray.find((item) => item.date.month === currentDate.getMonth() + 1 && item.date.year === currentDate.getFullYear())?.value || 0);

    return {
      fieldName: element.fieldName,
      color: element.color,
      value: value,
      unit: element.unit,
      price: element.price,
      ratio: 0,
    };
  }

  // Function to convert an array of StackedBarChartElement to an array of IDashboardDonutChartDataSet
  function convertArrayToDonutChartDataSetArray(
    elements: StackedBarChartElement[],
    currentDate: Date,
  ): IDashboardDonutChartDataSet[] {
    return elements.map((element) => convertToDonutChartDataSet(element, currentDate));
  }

  // For Supply with price toggle: recalculate monetary values based on actual/market price selection
  const adjustedAccumulated = React.useMemo(() => {
    if (!showPriceToggle || isQuantity) {
      // Use original data if:
      // - Not Supply (no price toggle)
      // - In quantity mode
      return accumulated;
    }

    if (isActualPrice) {
      // Actual price mode: use monetaryArray (contract prices)
      return accumulated.map(element => ({
        ...element,
        sumValue: element.sumMonetary, // Use actual/contract total
      }));
    }

    // Market price mode: recalculate monetary values using spot prices
    return accumulated.map(element => {
      const spotPrice = spotPricesMap[element.formulaId]?.price || 0;
      if (spotPrice === 0) return element; // No spot price available

      // Recalculate sumMonetary using spot price
      const sumMonetary = element.sumQuantity * spotPrice;

      return {
        ...element,
        sumMonetary,
        sumValue: sumMonetary, // For backward compatibility
      };
    });
  }, [accumulated, showPriceToggle, isQuantity, isActualPrice, spotPricesMap]);

  // Use adjusted accumulated data for donut chart
  const donutChartData = convertArrayToDonutChartDataSetArray(adjustedAccumulated, currentDate).map((item) => ({
    ...item,
    origVal: item.value,
    value: item.value, // accumulated data already contains correct values (quantity if isQuantity=true, monetary if isQuantity=false)
  }));

  const totalMonetaryAllFormulas = !isQuantity
    ? donutChartData.reduce((sum, item) => sum + (Number(item.value) || 0), 0)
    : 0;

  const materialExposures = donutChartData.filter((a) => !!a.fieldName);
  
  // Calculate totals - accumulated data already has correct values based on isQuantity flag
  const totalQuantityMT = isQuantity 
    ? materialExposures.reduce((sum, item) => sum + item.origVal, 0)
    : 0; // Not applicable in monetary mode

  // Compute both totals for dual-row donut center
  const isSpecificMode = formulaId !== GENERAL_ID;
  const totalMonetary = React.useMemo(
    () => adjustedAccumulated.filter(e => e.fieldName).reduce((s, e) => s + (e.sumMonetary || 0), 0),
    [adjustedAccumulated],
  );
  const totalQty = React.useMemo(
    () => adjustedAccumulated.filter(e => e.fieldName).reduce((s, e) => s + (e.sumQuantity || 0), 0),
    [adjustedAccumulated],
  );
  const qtyUnitLabel = isSpecificMode && materialsMap[formulaId]?.unit
    ? (getUnitObj(materialsMap[formulaId].unit)?.shortForm || 'MT')
    : 'MT';

  // Alternate total as secondary row: value mode shows qty, qty mode shows value
  const secondaryTitle = isSpecificMode
    ? (isQuantity
        ? FormatToShortPriceTemplate(totalMonetary)
        : `${formatToShortString(totalQty, 0)} ${qtyUnitLabel}`)
    : ''; // General mode: no secondary

  // For Supply with price toggle: recalculate monthly monetary values based on actual/market price selection
  const adjustedMonthExposures = React.useMemo(() => {
    if (!showPriceToggle || isQuantity || isActualPrice) {
      // Use original data if:
      // - Not Supply (no price toggle)
      // - In quantity mode
      // - Actual price selected (monetaryArray already has actual prices)
      return monthExposures;
    }

    // Market price mode: recalculate monetary values using spot prices
    return monthExposures.map(element => {
      const spotPrice = spotPricesMap[element.formulaId]?.price || 0;
      if (spotPrice === 0) return element; // No spot price available

      // Recalculate monetaryArray using spot price
      const monetaryArray = element.quantityArray.map(item => ({
        date: item.date,
        value: item.value * spotPrice,
      }));

      // Recalculate sumMonetary
      const sumMonetary = element.sumQuantity * spotPrice;

      return {
        ...element,
        monetaryArray,
        sumMonetary,
        valueArray: monetaryArray, // For backward compatibility
        sumValue: sumMonetary, // For backward compatibility
      };
    });
  }, [monthExposures, showPriceToggle, isQuantity, isActualPrice, spotPricesMap]);

  const currentBarData = adjustedMonthExposures && adjustedMonthExposures.length > 0 ? adjustedMonthExposures : emptyMockData;

  // Get formula display name for subtitle
  const formulaDisplayName = formulaId !== GENERAL_ID 
    ? (account?.formulasToAccount.find(f => f.formulaId === formulaId)?.displayName || materialsMap[formulaId]?.name || formulaId)
    : null;

  return (
    <div className="flex flex-col h-full min-w-[40rem] bg-white rounded-2xl shadow-2xl p-6 overflow-auto">
      <div className="flex justify-between items-start mb-2">
        <div className="flex flex-col">
          <h4 className="text-xl font-semibold text-gray-800">{cardTitle}</h4>
          {formulaDisplayName && (
            <p className="text-sm text-gray-500 mt-1">{formulaDisplayName} ({formulaId})</p>
          )}
        </div>
        {onClose && (
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 rounded-full transition-colors"
            aria-label="Close"
          >
            <Close className="text-gray-400 hover:text-gray-600" sx={{ fontSize: 24 }} />
          </button>
        )}
      </div>
      <div className="flex flex-col flex-1 justify-between pt-4">
        <div className="flex gap-4 items-start">
          {/* Donut Chart - fixed width to prevent layout jumping */}
          <div className="flex justify-center w-[45%] flex-shrink-0">
            <DashBoardDonutCard
              dataSet={donutChartData}
              secondaryTitle={secondaryTitle}
              ratioLine=""
              cssSize="w-52 h-52"
              isQuantity={isQuantity}
              quantityUnitLabel={isQuantity ? qtyUnitLabel : undefined}
            />
          </div>
          
          {/* Calculation / explanation panel - always reserve space to prevent jumping */}
          <div className={`flex-1 transition-opacity duration-200 ${isQuantity ? 'opacity-0 pointer-events-none' : 'opacity-100'}`}>
            {formulaId === GENERAL_ID ? (
              <div className="flex flex-col gap-1.5 text-sm text-gray-700">
                <div className="font-bold text-gray-900">
                  <div>
                    <span className="text-gray-400 text-sm mr-1">=</span>
                    $ {formatToShortString(totalMonetaryAllFormulas)}
                    <span className="text-xs ml-1">{baseCurrency}</span>
                  </div>
                  <div className="text-xs text-gray-500">Total value across all formulas</div>
                </div>
              </div>
            ) : (
              (() => {
                const exampleFormula = monthExposures[0];
                if (!exampleFormula) return null;
                
                const spotPriceInfo = spotPricesMap[exampleFormula.formulaId];
                const quantity = exampleFormula.sumQuantity;
                const actualPrice = exampleFormula.sumMonetary / (quantity || 1);
                const spotPrice = spotPriceInfo?.price || 0;
                const originalCurrency = spotPriceInfo?.originalCurrency || baseCurrency;
                const originalPrice = spotPriceInfo?.originalPrice || spotPrice;
                const rawUnit = materialsMap[exampleFormula.formulaId]?.unit;
                const unit = rawUnit ? (getUnitObj(rawUnit)?.shortForm || 'MT') : 'MT';
                
                const displayTotal = (showPriceToggle && isActualPrice) ? exampleFormula.sumMonetary : (quantity * spotPrice);
                
                return (
                  <div className="flex flex-col gap-1.5 text-sm">
                    {showPriceToggle && isActualPrice ? (
                      <>
                        <div>
                          <div className="font-semibold text-gray-900">{formatToShortString(quantity)} {unit}</div>
                          <div className="text-xs text-gray-500">Total Quantity</div>
                        </div>
                        
                        <div className="text-gray-900">
                          <div>
                            <span className="text-gray-400 text-sm mr-1">@</span>
                            <span className="font-semibold">{baseCurrency} {actualPrice.toFixed(2)}</span>
                            <span className="text-xs ml-1">{baseCurrency}/{unit}</span>
                          </div>
                          <div className="text-xs text-gray-500">Weighted Avg Price</div>
                        </div>
                        
                        <div className="font-bold text-gray-900">
                          <div>
                            <span className="text-gray-400 text-sm mr-1">=</span>
                            $ {formatToShortString(displayTotal)}
                            <span className="text-xs ml-1">{baseCurrency}</span>
                          </div>
                          <div className="text-xs text-gray-500">Total Value</div>
                        </div>
                      </>
                    ) : (
                      <>
                        <div>
                          <div className="font-semibold text-gray-900">{formatToShortString(quantity)} {unit}</div>
                          <div className="text-xs text-gray-500">Total Quantity</div>
                        </div>
                        
                        <div className="text-blue-600">
                          <div>
                            <span className="text-gray-400 text-sm mr-1">×</span>
                            <span className="font-semibold">
                              {originalCurrency} {originalPrice.toFixed(0)}
                            </span>
                            <span className="text-xs ml-1">{originalCurrency}/{unit}</span>
                          </div>
                          <div className="text-xs text-gray-500">Spot Price</div>
                        </div>
                        
                        {originalCurrency !== baseCurrency && (
                          <div className="text-purple">
                            <div>
                              <span className="text-gray-400 text-sm mr-1">×</span>
                              <span className="font-semibold">{(spotPrice / originalPrice).toFixed(4)}</span>
                              <span className="text-xs ml-1">{originalCurrency}/{baseCurrency}</span>
                            </div>
                            <div className="text-xs text-gray-500">FX Rate</div>
                          </div>
                        )}
                        
                        <div className="font-bold text-gray-900">
                          <div>
                            <span className="text-gray-400 text-sm mr-1">=</span>
                            $ {formatToShortString(displayTotal)}
                            <span className="text-xs ml-1">{baseCurrency}</span>
                          </div>
                          <div className="text-xs text-gray-500">Total Value</div>
                        </div>
                      </>
                    )}
                  </div>
                );
              })()
            )}
          </div>
        </div>
        <div className="flex h-16 items-center justify-center gap-3 text-sm" style={{ minWidth: 0 }}>
          {/* Value/Quantity Toggle */}
          {formulaId !== GENERAL_ID && (
            <div className="flex items-center gap-1 bg-gray-100 rounded-full p-1">
              <button
                onClick={() => setIsQuantity(false)}
                className={`px-3 py-1.5 rounded-full transition-colors flex items-center gap-1.5 ${
                  !isQuantity 
                    ? 'bg-white text-gray-900 shadow-sm font-medium' 
                    : 'text-gray-600 hover:text-gray-900'
                }`}
                title={isQuantity ? "Value" : undefined}
              >
                <AttachMoney className="w-4 h-4" />
                {!isQuantity && <span className="text-sm">Value</span>}
              </button>
              <button
                onClick={() => setIsQuantity(true)}
                className={`px-3 py-1.5 rounded-full transition-colors flex items-center gap-1.5 ${
                  isQuantity 
                    ? 'bg-white text-gray-900 shadow-sm font-medium' 
                    : 'text-gray-600 hover:text-gray-900'
                }`}
                title={!isQuantity ? "Quantity" : undefined}
              >
                <Inventory className="w-4 h-4" />
                {isQuantity && <span className="text-sm">Qty</span>}
              </button>
            </div>
          )}
          
          {/* Months/Terminals Toggle */}
          {(() => {
            // Only show terminals toggle if:
            // 1. Terminals exist
            // 2. Not all months are terminals (if all 12 months, no aggregation benefit)
            if (!terminals || terminals.length === 0) return null;
            const terminalCount = terminals.length;
            if (terminalCount >= 12) return null;
            
            return (
              <div className="flex items-center gap-1 bg-gray-100 rounded-full p-1">
                <button
                  onClick={() => setIsTerminals(false)}
                  className={`px-3 py-1.5 rounded-full transition-colors flex items-center gap-1.5 ${
                    !isTerminals 
                      ? 'bg-white text-gray-900 shadow-sm font-medium' 
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                  title={isTerminals ? "Months" : undefined}
                >
                  <DateRange className="w-4 h-4" />
                  {!isTerminals && <span className="text-sm">Months</span>}
                </button>
                <button
                  onClick={() => setIsTerminals(true)}
                  className={`px-3 py-1.5 rounded-full transition-colors flex items-center gap-1.5 ${
                    isTerminals 
                      ? 'bg-white text-gray-900 shadow-sm font-medium' 
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                  title={!isTerminals ? "Terminals" : undefined}
                >
                  <ViewWeek className="w-4 h-4" />
                  {isTerminals && <span className="text-sm">Terms</span>}
                </button>
              </div>
            );
          })()}
          
          {/* Market/Actual Price Toggle - always rendered when available to prevent layout jump */}
          {showPriceToggle && (
            <div className={`flex items-center gap-1 bg-gray-100 rounded-full p-1 transition-opacity duration-200 ${
              isQuantity ? 'opacity-30 pointer-events-none' : 'opacity-100'
            }`}>
              <button
                onClick={() => setIsActualPrice(false)}
                disabled={isQuantity}
                className={`px-3 py-1.5 rounded-full transition-colors flex items-center gap-1.5 ${
                  !isActualPrice 
                    ? 'bg-white text-gray-900 shadow-sm font-medium' 
                    : 'text-gray-600 hover:text-gray-900'
                }`}
                title={isActualPrice ? "Market Price" : undefined}
              >
                <ShowChart className="w-4 h-4" />
                {!isActualPrice && <span className="text-sm">Market</span>}
              </button>
              <button
                onClick={() => setIsActualPrice(true)}
                disabled={isQuantity}
                className={`px-3 py-1.5 rounded-full transition-colors flex items-center gap-1.5 ${
                  isActualPrice 
                    ? 'bg-white text-gray-900 shadow-sm font-medium' 
                    : 'text-gray-600 hover:text-gray-900'
                }`}
                title={!isActualPrice ? "Actual Price" : undefined}
              >
                <Receipt className="w-4 h-4" />
                {isActualPrice && <span className="text-sm">Actual</span>}
              </button>
            </div>
          )}
        </div>
        <div className="h-[12.5rem]">
          <StackedBarChart
            stackedBarChartData={currentBarData}
            terminals={isTerminals ? terminals : []}
            unit={''}
            isGeneral={formulaId === GENERAL_ID}
            setMaxVal={console.log}
            chartMax={0}
            showProportion={false}
            name="chart-details"
          />
        </div>
      </div>
    </div>
  );
};
